<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import SectionHead from '@/components/Stats/SectionHead.vue'
import GroupDistributionChart from '@/components/Stats/GroupStats/GroupSubjectInsights/GroupDistributionChart.vue'
import IndividualScoresChart from '@/components/Stats/GroupStats/GroupSubjectInsights/IndividualScoresChart.vue'
import UIKit from '@pocketprep/ui-kit'
import Dismissable from '@/components/Dismissable.vue'
import ContentVersionMessaging from '@/components/ContentVersionMessaging.vue'
import { groupModule } from '@/store/group/module'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import { type GroupAggStats } from '@/store/stats/types'
import { userModule } from '@/store/user/module'
import { isPromise } from '@/utils'
import { licenseModule } from '@/store/license/module'
import { analyticsModule } from '@/store/analytics/module'
import { organizationModule } from '@/store/organization/module'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'
import type { Teach } from '@pocketprep/types'

const {
    SegmentControl,
    Button: PocketButton,
} = UIKit

const chartType = ref<'Group Distribution' | 'Individual Scores' | null>(null)
const chartsOffsetTop = ref<number[]>([])
const viewportScrollTop = ref(window.innerHeight + window.scrollY + 20)
const showPurchaseSeatsModal = ref(false)
const charts = ref<HTMLElement[] | null>(null)

const currentGroup = computed((): Teach.Class.OrgGroupJSON | null => {
    return groupModule.getters.getCurrentGroup()
})

const groupAggStats = computed(() => statsModule.getters.getCurrentGroupAggStats())

const currentOrgLicenses = computed(() => licenseModule.getters.getCurrentOrgLicenses())

const numStudents = computed(() => {
    return currentGroup.value?.orgStudents.length || 0
})

const currentExamMetadata = computed(() => examModule.getters.getCurrentExamMetadata())

const areAllOrgExamsFree = computed(() => examModule.getters.getAreAllOrgExamsFree())

const isLoading = computed(() => {
    return !groupAggStats.value || isPromise(groupAggStats.value) || groupAggStats.value.isLoading
})

const sortedSubjectInsights = computed((): GroupAggStats['subjectInsights'] => {
    if (groupAggStats.value.subjectInsights.length) {
        const grpAggStats = groupAggStats.value
        return grpAggStats.subjectInsights.sort((a, b) => {
            const aAverageScore = a.scores.reduce((acc, score) => (acc + score.scorePct), 0) / a.scores.length
            const bAverageScore = b.scores.reduce((acc, score) => (acc + score.scorePct), 0) / b.scores.length
            return aAverageScore - bAverageScore
        })
    } else if (currentExamMetadata.value) {
        const quantiles = [
            '0-10',
            '10-20',
            '20-30',
            '30-40',
            '40-50',
            '50-60',
            '60-70',
            '70-75',
            '75-80',
            '80-90',
            '90-100',
        ] as const
        const zeroedQuantiles = quantiles.reduce((zeroed, quantile) => ({
            ...zeroed,
            [quantile]: 0,
        }), {} as { [key in (typeof quantiles)[number]]: 0 })

        return Object.values(currentExamMetadata.value.knowledgeAreas).map(ka => ({
            subjectName: ka.name,
            distribution: {
                ...zeroedQuantiles,
                countBelow50: 0,
                count50to75: 0,
                countAbove75: 0,
            },
            scores: [],
        })).sort((a, b) => a.subjectName.localeCompare(b.subjectName))
    } else {
        return []
    }
})

const currentOrganization = computed(() => organizationModule.getters.getCurrentOrganization())

const showPurchaseSeatsBanner = computed(() => {
    // Only show if the current organization is newer than May 1, 2024 and has 0 licenses
    return !areAllOrgExamsFree.value
        && currentOrgLicenses.value.length === 0
        && organizationModule.getters.getHasSelfServicePaymentAccess()
})

const closePurchaseSeatsModal = () => {
    showPurchaseSeatsModal.value = false
}

const watchDemo = () => {
    window.open('https://www.youtube.com/watch?v=nfBQXVyBg_E')

    analyticsModule.actions.amplitudeTrack('CTA Clicked', {
        ctaLabel: 'Watch a Demo',
        ctaPlacement: 'stats',
        // Type coercion here is fine since it's really unlikely this'll not be defined
        organizationId: currentOrganization.value?.objectId as string,
        organizationName: currentOrganization.value?.name as string,
    })
}

const showPurchaseSeatsSidePanel = () => {
    showPurchaseSeatsModal.value = true

    if (currentOrganization.value) {
        // Fire amplitude event
        analyticsModule.actions.amplitudeTrack('Side Panel Opened', {
            sidePanelName: 'Purchase Seats',
            sidePanelPlacement: 'Group Subject Insights',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }
}

const scrollListener = () => {
    const scrollContainer = document.querySelector('.skeleton > .skeleton-body') as HTMLElement
    viewportScrollTop.value = scrollContainer.scrollTop + window.innerHeight + 20
}

const updateChartType = (type: number) => {
    const newChartType = type === 1 ? 'Group Distribution' : 'Individual Scores'
    chartType.value = newChartType
    analyticsModule.actions.amplitudeTrack('Segment Control Clicked', {
        segmentControlContext: 'Group Subject Insights Chart Type',
        segmentControlValue: newChartType,
    })
}

onMounted(async () => {
    chartType.value = 'Individual Scores'
    await Promise.all([
        examModule.actions.fetchExamMetadata(),
        examModule.actions.fetchCurrentGroupExam(),
    ])

    await nextTick()
    const chartsEls = charts.value
    const chartsArray = Array.isArray(chartsEls) ? chartsEls : (chartsEls ? [ chartsEls ] : [])
    chartsOffsetTop.value = chartsArray.map((chart: HTMLElement) => chart.offsetTop)

    await Promise.all([
        groupModule.actions.fetchCurrentGroup(),
        statsModule.actions.fetchCurrentGroupAggStats(),
    ])

    if (numStudents.value >= 40) {
        updateChartType(1)
    }

    const scrollContainer = document.querySelector('.skeleton > .skeleton-body') as HTMLElement
    scrollContainer.addEventListener('scroll', scrollListener)

    if (numStudents.value > 0) {
        userModule.actions.dismiss('groupStatsSneakPeak')
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('scroll', scrollListener)
})
</script>

<template>
    <div
        class="group-subject-insights"
    >
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="closePurchaseSeatsModal"
        />
        <Dismissable
            v-if="!isLoading && showPurchaseSeatsBanner"
            dismissable-id="groupStatsSneakPeak"
            title="Get started using your Instructor Dashboard account"
        >
            Purchase exam prep and add your students. Once students are activated you will be able to track
            their progress, assign them quizzes, or just sit back and feel good about providing exam prep that works!
            <template #confirmation>
                <PocketButton
                    class="group-subject-insights__confirmation-text"
                    type="tertiary-small"
                    @click="showPurchaseSeatsSidePanel"
                >
                    Purchase for Students
                </PocketButton>
                <span
                    class="group-subject-insights__bullet"
                >  •  </span>
                <PocketButton
                    class="group-subject-insights__confirmation-text"
                    type="tertiary-small"
                    @click="watchDemo"
                >
                    Watch a Demo
                </PocketButton>
            </template>
        </Dismissable>
        <Dismissable
            v-else-if="!isLoading && numStudents === 0"
            dismissable-id="groupStatsSneakPeak"
            title="Here's a sneak peek"
        >
            Go ahead and scroll down to get an idea of what you will be able to see.
            These are placeholder graphs until you have students.
        </Dismissable>
        <ContentVersionMessaging v-if="!isLoading" />
        <SectionHead
            class="group-subject-insights__section-head"
            title="Subject Insights"
            :icon="'subject'"
        >
            <SegmentControl
                :default-option="chartType === 'Group Distribution' ? 1 : 2"
                option1="Group Distribution"
                option2="Individual Scores"
                @change="updateChartType"
            />
        </SectionHead>
        <div
            v-for="(subjectInsight, index) in sortedSubjectInsights"
            :key="`chart-${subjectInsight.subjectName}-${subjectInsight.scores.length}`"
            ref="charts"
            class="group-subject-insights__chart"
        >
            <GroupDistributionChart
                v-if="chartType === 'Group Distribution'
                    && chartsOffsetTop[index] !== undefined
                    && viewportScrollTop > chartsOffsetTop[index]"
                class="group-subject-insights__distribution"
                :subject-name="subjectInsight.subjectName"
                :distribution="subjectInsight.distribution"
            />
            <IndividualScoresChart
                v-if="chartType === 'Individual Scores'
                    && chartsOffsetTop[index] !== undefined
                    && viewportScrollTop > chartsOffsetTop[index]"
                :key="`individual-${subjectInsight.subjectName}-${subjectInsight.scores.length}`"
                class="group-subject-insights__individual"
                :subject-name="subjectInsight.subjectName"
                :scores="subjectInsight.scores"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.group-subject-insights {
    .dismissable {
        margin-bottom: 19px;
    }

    &__confirmation-text {
        font-weight: 600 !important;
        font-size: 14px !important;
        line-height: 19px !important;
        padding-left: 0 !important;
    }

    &__bullet {
        margin: 3px;
        font-size: 14px;
        color: $pewter;
    }

    &__section-head {
        margin-bottom: 27px;
    }

    &__loading {
        margin-bottom: 40px;
    }

    &__chart {
        height: 182px;
        margin-bottom: 20px;
    }
}
</style>
